import React, { useState, useEffect } from "react";
import {
  Modal,
  Grid,
  Typography,
  Stack,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  NormalTextField,
  HeaderButton,
  DropdownTextField,
  DateTextField,
  CountryTextField,
} from "../../../components";
import { MuiTelInput } from "mui-tel-input";
import dayjs from "dayjs";
import { api, urls } from "../../../services";
import { useToast, useAuth } from "../../../contexts";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 700,
    maxHeight: "90vh",
    overflowY: "auto",
    background: theme.palette.common.greyBg,
    boxShadow: 24,
    padding: theme.spacing(4),
    borderRadius: 6,
  },
  titleText: {
    "&.MuiTypography-root": {
      fontSize: 20,
      fontWeight: 600,
    },
    display: "flex",
    justifyContent: "center",
    paddingBottom: theme.spacing(3),
  },
  sectionTitle: {
    "&.MuiTypography-root": {
      fontSize: 16,
      fontWeight: 600,
      paddingTop: theme.spacing(1),
    },
  },
}));

export function EditUser(props) {
  const classes = useStyles();
  const { open, onClose, onUpdated, userData } = props;
  const { showToast } = useToast();
  const { logout } = useAuth();

  const [formData, setFormData] = useState({
    first_name: userData?.first_name || "",
    email: userData?.email || "",
    phone_code: userData?.phone_code || "+91",
    phone_number: userData?.phone_number || "",
    country: userData?.country || "",
    state: userData?.state || "",
    religion: userData?.religion || "",
    occupation: userData?.occupation || "",
  });

  const [planData, setPlanData] = useState({
    subscription_plan: null,
    startDate: null,
    endDate: null,
  });

  const [deactivate, setDeactivate] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [apiCalling, setApiCalling] = useState(false);

  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (value) => {
    let items = value.split(" ");
    let code = items[0];
    let phone = items.slice(1).join("");
    setFormData((prev) => ({ ...prev, phone_code: code, phone_number: phone }));
  };

  const handlePlanChange = (e, value) => {
    setPlanData((prev) => ({ ...prev, subscription_plan: value }));
  };

  const handleStartDateChange = (date) => {
    const startDate = new Date(date);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 364);
    setPlanData((prev) => ({ ...prev, startDate, endDate }));
  };

  const handleSaveClick = () => {
    let params = {
      first_name: formData.first_name,
      email: formData.email,
      phone_code: formData.phone_code,
      phone_number: formData.phone_number,
      country: formData.country,
      state: formData.state,
      religion: formData.religion,
      occupation: formData.occupation,
    };

    if (deactivate) {
      params.deactivate_subscription = true;
    }

    if (planData.subscription_plan && planData.startDate && planData.endDate) {
      params.subscription_plan = planData.subscription_plan.id;
      params.plan_start_date = dayjs(planData.startDate).format("YYYY-MM-DD");
      params.plan_end_date = dayjs(planData.endDate).format("YYYY-MM-DD");
    }

    setApiCalling(true);
    api
      .post(urls.editUser + userData.id, params)
      .then((res) => {
        setApiCalling(false);
        showToast("User updated successfully", "success");
        onUpdated();
      })
      .catch((err) => {
        setApiCalling(false);
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  };

  useEffect(() => {
    api
      .get(`${urls.subscriptionList}?page=1&limit=50`)
      .then((res) => {
        const subs = res.data.data.subscription_plans;
        setSubscriptions(subs);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          logout();
        }
      });
  }, []);

  return (
    <Modal open={open}>
      <Box className={classes.root}>
        <Typography className={classes.titleText}>Edit User</Typography>
        <Grid container spacing={4} direction="column">
          <Grid item xs>
            <Stack spacing={3}>
              <Stack direction="row" spacing={2}>
                <NormalTextField
                  label="Name"
                  value={formData.first_name}
                  onChange={(e) =>
                    handleFieldChange("first_name", e.target.value)
                  }
                />
                <MuiTelInput
                  label="Phone Number"
                  sx={{ width: "100%" }}
                  defaultCountry="IN"
                  value={formData.phone_code + " " + formData.phone_number}
                  onChange={handlePhoneChange}
                />
              </Stack>
              <NormalTextField
                label="Email"
                value={formData.email}
                onChange={(e) => handleFieldChange("email", e.target.value)}
              />
              <Stack direction="row" spacing={2}>
                <CountryTextField
                  label="Country"
                  value={formData.country}
                  onChange={(e, n) =>
                    handleFieldChange("country", n ? n.label : "")
                  }
                />
                <NormalTextField
                  label="State"
                  value={formData.state}
                  onChange={(e) => handleFieldChange("state", e.target.value)}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <NormalTextField
                  label="Religion"
                  value={formData.religion}
                  onChange={(e) =>
                    handleFieldChange("religion", e.target.value)
                  }
                />
                <NormalTextField
                  label="Occupation"
                  value={formData.occupation}
                  onChange={(e) =>
                    handleFieldChange("occupation", e.target.value)
                  }
                />
              </Stack>

              <Typography className={classes.sectionTitle}>
                Subscription Management
              </Typography>

              {userData?.subscriptions?.length > 0 && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={deactivate}
                      onChange={(e) => setDeactivate(e.target.checked)}
                      color="error"
                    />
                  }
                  label="Deactivate Current Subscription"
                />
              )}

              <Stack spacing={2} direction="row">
                <DropdownTextField
                  label="Activate Plan"
                  options={subscriptions}
                  values={planData.subscription_plan}
                  noClearIcon={false}
                  optionKey="name"
                  onChange={handlePlanChange}
                />
                <Stack spacing={2} direction="row" sx={{ width: "100%" }}>
                  <DateTextField
                    label="Start Date"
                    onChange={handleStartDateChange}
                    value={
                      planData.startDate === null
                        ? null
                        : dayjs(planData.startDate)
                    }
                  />
                  <DateTextField
                    label="End Date"
                    value={
                      planData.endDate === null
                        ? null
                        : dayjs(planData.endDate)
                    }
                    disabled
                  />
                </Stack>
              </Stack>

              <Stack direction="row" spacing={2}>
                <HeaderButton type="secondary" onClick={onClose}>
                  Cancel
                </HeaderButton>
                <HeaderButton
                  type="primary"
                  onClick={handleSaveClick}
                  disabled={apiCalling}
                >
                  Save Changes
                </HeaderButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
